import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"

interface SidebarContextType {
  isCollapsed: boolean
  toggleSidebar: () => void
  sidebarWidth: number
  setSidebarWidth: (width: number) => void
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleSidebar: () => {},
  sidebarWidth: 275,
  setSidebarWidth: () => {},
})

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

interface SidebarProviderProps {
  children: ReactNode
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed")
    return stored === "true"
  })

  const [sidebarWidth, setSidebarWidth] = useState(isCollapsed ? 120 : 275)

  const toggleSidebar = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev
      localStorage.setItem("sidebar-collapsed", String(newValue))
      setSidebarWidth(newValue ? 120 : 275)
      return newValue
    })
  }

  useEffect(() => {
    // Update CSS variable for smooth transitions
    document.documentElement.style.setProperty(
      "--sidebar-width",
      `${sidebarWidth}px`,
    )
  }, [sidebarWidth])

  return (
    <SidebarContext.Provider
      value={{ isCollapsed, toggleSidebar, sidebarWidth, setSidebarWidth }}
    >
      {children}
    </SidebarContext.Provider>
  )
}
